import { Controller } from "@hotwired/stimulus"

// Drives the mobile nav overlay (#main_menu). Replaces the generic reveal toggle
// so the panel slides down from the top instead of snapping into view.
//
// `hidden` stays the source of truth for open/closed (scroll_lock watches it to
// lock the body, which also clips the off-screen panel during the slide). On top
// of that we toggle an `is-open` class one frame later that the CSS animates:
// reveal off-screen, force a reflow so the start frame paints, then slide in. On
// close we slide back out first and only re-add `hidden` once the panel is off
// screen, so the exit isn't cut off by display:none.
//
// The trigger button (`button` target) sits above the panel and morphs its
// hamburger bars into a cross while the panel is open. Driving that class from
// here — rather than a separate controller wired to each click — keeps the icon
// in sync however the panel closes (button tap, Escape, etc.).
export default class extends Controller {
  static targets = ["panel", "button"]

  toggle() {
    if (this.panelTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    if (this.closeTimer) { clearTimeout(this.closeTimer); this.closeTimer = null }
    this.panelTarget.classList.remove("hidden")
    // Skip the reflow under reduced motion so there's no one-frame flash of the
    // off-screen start state before it snaps into place.
    if (!this.reducedMotion) void this.panelTarget.offsetWidth
    this.panelTarget.classList.add("is-open")
    this.setButtonActive(true)
  }

  close() {
    this.panelTarget.classList.remove("is-open")
    this.setButtonActive(false)
    clearTimeout(this.closeTimer)
    if (this.reducedMotion) {
      this.panelTarget.classList.add("hidden")
      return
    }
    // Restore display:none once the slide-out finishes (matches the 150ms CSS
    // transition) so scroll_lock unlocks the body and the parked panel can't add
    // horizontal overflow.
    this.closeTimer = setTimeout(() => {
      this.panelTarget.classList.add("hidden")
      this.closeTimer = null
    }, 150)
  }

  setButtonActive(active) {
    if (!this.hasButtonTarget) return
    this.buttonTarget.classList.toggle("is-active", active)
    this.buttonTarget.setAttribute("aria-expanded", active ? "true" : "false")
  }

  get reducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }
}
